import { isPreviewAdminMode, isPreviewProMode } from '../lib/preview';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from 'react';
import type { Session, User } from '@supabase/supabase-js';
import type { AccountType, Profile } from '../types';
import type { WorkspaceUser } from '../lib/workspace';
import { configured, supabase } from '../lib/supabase';
import {
  getSession,
  loadAccountAccess,
  loadAccountType,
  loadSubscriptionTier,
  isDeviceMarkedAdmin,
  setDeviceAdmin,
  ADMIN_EMAILS_LOWER,
  professionalListClients,
  professionalAcceptInvitation,
  professionalRejectInvitation,
  signOut as cloudSignOut,
  type ProfessionalClient,
  type SubscriptionTier,
} from '../lib/cloud';
import { hasTrustedAdminClaim } from '../lib/admin-access';

export interface PendingAdvisorInvite {
  advisorId: string;
  advisorName?: string;
  advisorEmail?: string;
}

export interface AuthContextValue {
  session: Session | null;
  user: WorkspaceUser | null;
  setUser: Dispatch<SetStateAction<WorkspaceUser | null>>;
  accountType: AccountType;
  setAccountType: Dispatch<SetStateAction<AccountType>>;
  subscriptionTier: SubscriptionTier;
  setSubscriptionTier: Dispatch<SetStateAction<SubscriptionTier>>;
  isAdmin: boolean;
  setIsAdmin: Dispatch<SetStateAction<boolean>>;
  isDeviceAdmin: boolean;
  setIsDeviceAdmin: (val: boolean) => void;
  accountReady: boolean;
  proClients: ProfessionalClient[];
  setProClients: Dispatch<SetStateAction<ProfessionalClient[]>>;
  refreshClients: () => Promise<void>;
  refreshAccountAccess: () => Promise<void>;
  pendingAdvisorInvite: PendingAdvisorInvite | null;
  setPendingAdvisorInvite: Dispatch<SetStateAction<PendingAdvisorInvite | null>>;
  acceptAdvisorInvite: (advisorId: string) => Promise<void>;
  rejectAdvisorInvite: () => Promise<void>;
  profile: Profile;
  signOut: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

export interface AuthProviderProps {
  demo?: boolean | { page?: string; basket?: boolean };
  initialUser?: WorkspaceUser | null;
  children: ReactNode;
}

const isPreviewAdmin = isPreviewAdminMode();
const isPreviewPro = isPreviewProMode();

export function AuthProvider({ demo, initialUser, children }: AuthProviderProps) {
  const isDemo = Boolean(demo);
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<WorkspaceUser | null>(() => {
    if (initialUser !== undefined) return initialUser;
    if (isDemo) return { id: 'demo', email: 'eksempel@plotwise.dk' };
    if (isPreviewAdmin) return { id: 'preview-admin', email: 'admin@plotwise.dk' };
    if (isPreviewPro) return { id: 'preview-pro', email: 'emil.beiner@plotwise.dk' };
    return null;
  });

  const [isAdmin, setIsAdmin] = useState(isPreviewAdmin);
  const [isDeviceAdminState, setIsDeviceAdminState] = useState(() => isDeviceMarkedAdmin());
  const [accountReady, setAccountReady] = useState(isPreviewAdmin || isPreviewPro);
  // Først når sessionen er læst fra lageret ved vi, om der er en bruger. Før da må kontoen ikke
  // meldes klar, ellers ser appen en tom 'standard'-konto og sender fx en rådgiver til kortet.
  const [sessionChecked, setSessionChecked] = useState(
    () => isDemo || isPreviewAdmin || isPreviewPro || !configured || !supabase,
  );
  const [accountType, setAccountType] = useState<AccountType>(
    isPreviewAdmin || isPreviewPro ? 'professional' : 'standard',
  );
  const [subscriptionTier, setSubscriptionTier] = useState<SubscriptionTier>(() =>
    isPreviewAdmin || isPreviewPro ? 'pro' : 'free',
  );
  const [proClients, setProClients] = useState<ProfessionalClient[]>([]);
  const [pendingAdvisorInvite, setPendingAdvisorInvite] = useState<PendingAdvisorInvite | null>(
    null,
  );
  const currentUserId = useRef(user?.id);
  currentUserId.current = user?.id;

  const refreshAccountAccess = useCallback(async () => {
    if (!user || isDemo || isPreviewAdmin || isPreviewPro) return;
    const access = await loadAccountAccess(user.id);
    if (currentUserId.current !== user.id) return;
    setAccountType(access.accountType);
    setSubscriptionTier(access.subscriptionTier);
    if (access.accountType !== 'professional') setProClients([]);
  }, [user?.id, isDemo]);

  const markDeviceAdmin = useCallback((val: boolean) => {
    setDeviceAdmin(val);
    setIsDeviceAdminState(val);
  }, []);

  // Lyt på Supabase auth session ændringer
  useEffect(() => {
    if (isDemo || !configured || !supabase) {
      if (isPreviewAdmin) {
        setIsAdmin(true);
        setAccountReady(true);
        setAccountType('professional');
        setSubscriptionTier('pro');
      } else if (isPreviewPro) {
        setIsAdmin(false);
        setAccountReady(true);
        setAccountType('professional');
        setSubscriptionTier('pro');
      }
      return;
    }

    let active = true;
    const { data: authSub } = supabase.auth.onAuthStateChange((_event, newSession) => {
      if (!active) return;
      setSession(newSession);
      const u: WorkspaceUser | null = newSession?.user
        ? { id: newSession.user.id, email: newSession.user.email ?? '' }
        : null;
      setUser(u);
      setSessionChecked(true);
    });

    void getSession()
      .then((sess) => {
        if (!active) return;
        setSession(sess);
        const u: WorkspaceUser | null = sess?.user
          ? { id: sess.user.id, email: sess.user.email ?? '' }
          : null;
        setUser(u);
        setSessionChecked(true);
      })
      .catch(() => {
        if (active) setSessionChecked(true);
      });

    return () => {
      active = false;
      authSub.subscription.unsubscribe();
    };
  }, [isDemo]);

  // Håndter admin-status, kontotype og invitationer når brugeren ændres
  useEffect(() => {
    let active = true;
    if (isPreviewAdmin) {
      setIsAdmin(true);
      setAccountReady(true);
      setAccountType('professional');
      setSubscriptionTier('pro');
      return;
    }
    if (isPreviewPro) {
      setIsAdmin(false);
      setAccountReady(true);
      setAccountType('professional');
      setSubscriptionTier('pro');
      return;
    }
    if (isDemo) {
      setIsAdmin(false);
      setAccountType('standard');
      setSubscriptionTier('free');
      setAccountReady(true);
      return;
    }

    setIsAdmin(false);
    setAccountType('standard');
    setSubscriptionTier('free');

    if (!user || !supabase) {
      setAccountReady(sessionChecked);
      return;
    }
    setAccountReady(false);

    void supabase.auth.getUser().then(async ({ data, error }) => {
      if (!active || error || data.user?.id !== user.id) return;
      const admin = hasTrustedAdminClaim(data.user);
      setIsAdmin(admin);

      if (admin) {
        setAccountType('professional');
        setSubscriptionTier('pro');
        setAccountReady(true);
        markDeviceAdmin(true);
        void professionalListClients()
          .then((clients) => {
            if (active) setProClients(clients);
          })
          .catch(() => {});
        return;
      }

      // Tjek om der er en ventende rådgiver-invitation til brugeren
      const meta = (data.user?.user_metadata || {}) as Record<string, unknown>;
      const pendingInvite = meta.pending_advisor_invitation as
        { advisor_id?: string; advisor_name?: string; advisor_email?: string } | undefined;
      const urlParams =
        typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
      const urlAdvisorId = urlParams?.get('accept_advisor');
      if (urlAdvisorId || (pendingInvite && pendingInvite.advisor_id)) {
        setPendingAdvisorInvite({
          advisorId: urlAdvisorId || (pendingInvite?.advisor_id as string),
          advisorName: pendingInvite?.advisor_name,
          advisorEmail: pendingInvite?.advisor_email,
        });
      }

      try {
        const { accountType: type, subscriptionTier: tier } = await loadAccountAccess(user.id);
        if (!active) return;
        setAccountType(type);
        setSubscriptionTier(tier);
        setAccountReady(true);
        if (type === 'professional') {
          void professionalListClients()
            .then((clients) => {
              if (active) setProClients(clients);
            })
            .catch(() => {});
        }
      } catch {
        if (active) {
          setAccountType('standard');
          setSubscriptionTier('free');
          setAccountReady(true);
        }
      }
    });

    return () => {
      active = false;
    };
  }, [
    user?.id,
    isDemo,
    markDeviceAdmin,
    sessionChecked,
    session?.user.app_metadata?.subscription_tier,
    session?.user.app_metadata?.plotwise_admin,
  ]);

  // Betaling og opsigelse kan ske på en anden enhed uden at ændre denne browsers token.
  useEffect(() => {
    if (!user || isDemo || isPreviewAdmin || isPreviewPro) return;
    const refresh = () => {
      if (document.visibilityState === 'hidden') return;
      void refreshAccountAccess().catch((error: unknown) => {
        if (currentUserId.current !== user.id) return;
        setAccountType('standard');
        setSubscriptionTier('free');
        setProClients([]);
        console.error('Abonnementsadgangen kunne ikke kontrolleres', error);
      });
    };
    const timer = window.setInterval(refresh, 60_000);
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [user?.id, isDemo, refreshAccountAccess]);

  const acceptAdvisorInvite = useCallback(async (advisorId: string) => {
    await professionalAcceptInvitation(advisorId);
    setPendingAdvisorInvite(null);
  }, []);

  const rejectAdvisorInvite = useCallback(async () => {
    await professionalRejectInvitation();
    setPendingAdvisorInvite(null);
  }, []);

  const refreshClients = useCallback(async () => {
    if (user && (accountType === 'professional' || isAdmin)) {
      try {
        const clients = await professionalListClients();
        setProClients(clients);
      } catch {
        // ignore
      }
    }
  }, [user?.id, accountType, isAdmin]);

  const signOut = useCallback(async () => {
    if (!configured || !supabase) return;
    await cloudSignOut();
    setUser(null);
    setSession(null);
    setIsAdmin(false);
    setAccountType('standard');
    setSubscriptionTier('free');
    setProClients([]);
    setPendingAdvisorInvite(null);
  }, []);

  const profile = useMemo<Profile>(
    () => ({
      name: user?.email ? user.email.split('@')[0] : '',
      email: user?.email ?? '',
      company: '',
    }),
    [user?.email],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      user,
      setUser,
      accountType,
      setAccountType,
      subscriptionTier,
      setSubscriptionTier,
      isAdmin,
      setIsAdmin,
      isDeviceAdmin: isDeviceAdminState,
      setIsDeviceAdmin: markDeviceAdmin,
      accountReady,
      proClients,
      setProClients,
      refreshClients,
      refreshAccountAccess,
      pendingAdvisorInvite,
      setPendingAdvisorInvite,
      acceptAdvisorInvite,
      rejectAdvisorInvite,
      profile,
      signOut,
    }),
    [
      session,
      user,
      accountType,
      subscriptionTier,
      isAdmin,
      isDeviceAdminState,
      markDeviceAdmin,
      accountReady,
      proClients,
      refreshClients,
      refreshAccountAccess,
      pendingAdvisorInvite,
      acceptAdvisorInvite,
      rejectAdvisorInvite,
      profile,
      signOut,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth skal anvendes inden i en AuthProvider');
  }
  return context;
}
